import Highcharts from "highcharts";
import * as io from "io-ts";
import { useCallback, useEffect, useMemo } from "react";
import createGlobalState from "@ui/lib/createGlobalState";
import { Period, type PeriodT } from "../../../../../../schema/period";
import { syncPointPeriod, syncTemporalAxesPeriod } from "../utils/period";
import { useLinkedPlotList, usePlotLink } from "./usePlotLink";
import { type BlockPlotModelT } from "../../../../../../schema/block/plot";
import { type Options } from "@ui/Highcharts";

export const useLocalPlotPeriod = createGlobalState({
  key: "dashboardLocalPlotPeriod",
  storage: "session",
  type: io.union([Period, io.null]),
  scoped: true,
});

export const useSharedPlotPeriod = createGlobalState({
  key: "dashboardSharedPlotPeriod",
  storage: "session",
  type: io.union([Period, io.null]),
  scoped: true,
});

/**
 * @description
 * Retrieve plot period getter and setter,
 * either from local state or global (shared)
 * state depending on plot linked state.
 */
export const usePlotPeriod = (
  block: BlockPlotModelT,
): [PeriodT | null, (period: PeriodT) => void] => {
  const { isLinked } = usePlotLink(block);

  const [sharedPlotPeriod, setSharedPlotPeriod] = useSharedPlotPeriod({
    defaultValue: null,
    scope: block.dashboardId,
  });

  const [localPlotPeriod, setLocalPlotPeriod] = useLocalPlotPeriod({
    defaultValue: block.config.options?.temporalZoom || null,
    scope: block.id,
  });

  const [linkedPlotList] = useLinkedPlotList({ scope: block.dashboardId });

  const setSelectedPeriod = useCallback(
    (period: PeriodT) => {
      const p =
        typeof period === "string" ? period : { from: period.from || null, to: period.to || null };
      if (isLinked) {
        setSharedPlotPeriod(p);
      } else {
        setLocalPlotPeriod(p);
      }
    },
    [isLinked, setSharedPlotPeriod, setLocalPlotPeriod],
  );

  useEffect(() => {
    if (isLinked) {
      setLocalPlotPeriod(sharedPlotPeriod);
    }
  }, [sharedPlotPeriod, isLinked, setLocalPlotPeriod]);

  const selectedPeriod = useMemo(() => {
    const period = isLinked ? sharedPlotPeriod : localPlotPeriod;
    if (period === null) return { from: undefined, to: undefined };
    if (typeof period === "string") return period;
    return { from: period.from || undefined, to: period.to || undefined };
  }, [isLinked, localPlotPeriod, sharedPlotPeriod]);

  // when link plot, if shared === null, set shared to local
  // when unlink plot, set local === shared
  useEffect(() => {
    if (
      linkedPlotList.length === 1 &&
      linkedPlotList[0] === block.id &&
      localPlotPeriod &&
      !sharedPlotPeriod
    ) {
      return setSharedPlotPeriod(localPlotPeriod);
    }
    if (linkedPlotList.length === 0) {
      return setSharedPlotPeriod(null);
    }
  }, [
    block.id,
    linkedPlotList,
    localPlotPeriod,
    setLocalPlotPeriod,
    setSharedPlotPeriod,
    sharedPlotPeriod,
  ]);

  return [selectedPeriod, setSelectedPeriod];
};

/**
 * @description
 * Set local or shared plot period from zoom event,
 * depending on the plot linked state.
 */
export const usePlotPeriodFromZoomEffect = ({
  block,
  plot,
  options,
}: {
  block: BlockPlotModelT;
  plot: React.RefObject<Highcharts.Chart>;
  options: Options | null;
}) => {
  const [_, setSelectedPeriod] = usePlotPeriod(block);

  const setSelectedZoomPeriod = useCallback(
    (e: Highcharts.AxisSetExtremesEventObject) => {
      if (e.trigger === "zoom" || e.trigger === "navigator" || e.trigger === "DEFAULT") {
        setSelectedPeriod({ from: e.min, to: e.max });
      }
    },
    [setSelectedPeriod],
  );

  useEffect(() => {
    if (!plot.current || !options) return;
    const p = plot.current;

    const temporalAxes = p?.axes.filter(
      axis =>
        !axis.options.id?.startsWith("navigator") &&
        axis.options.type === "datetime" &&
        axis.series.length > 0,
    );

    for (const axis of temporalAxes) {
      Highcharts.removeEvent(
        axis,
        "afterSetExtremes",
        setSelectedZoomPeriod as unknown as Highcharts.EventCallbackFunction<Highcharts.Axis>,
      );
      Highcharts.addEvent(axis, "afterSetExtremes", setSelectedZoomPeriod);
    }

    return () => {
      for (const axis of temporalAxes) {
        Highcharts.removeEvent(
          axis,
          "afterSetExtremes",
          setSelectedZoomPeriod as unknown as Highcharts.EventCallbackFunction<Highcharts.Axis>,
        );
      }
    };
  }, [options, plot, setSelectedZoomPeriod]);
};

/**
 * @description
 * Synchronize plot period from local or shared state,
 * depending on the plot linked state.
 *
 * @todo
 * This currently works only for temporal axes.
 * But we should be able to make it work for any axis referring data date.
 */
export const usePlotPeriodSyncEffect = ({
  block,
  datasetPeriodRange,
  plot,
  cycle,
}: {
  block: BlockPlotModelT;
  datasetPeriodRange: { from: number; to: number };
  plot: React.RefObject<Highcharts.Chart>;
  cycle?: { from: number; to: number };
}) => {
  const [selectedPeriod] = usePlotPeriod(block);

  useEffect(() => {
    if (selectedPeriod === null) return;

    const temporalAxes =
      plot.current?.axes.filter(
        axis =>
          !axis.options.id?.startsWith("navigator") &&
          axis.options.type === "datetime" &&
          axis.series.length > 0,
      ) ?? [];

    /** @note sync timeseries */
    if (temporalAxes && temporalAxes.length > 0) {
      syncTemporalAxesPeriod(temporalAxes, selectedPeriod, datasetPeriodRange, cycle);
      return;
    }

    /** @note sync non-timeseries */
    syncPointPeriod(
      plot.current,
      plot.current?.series.flatMap(s => s.data) ?? [],
      datasetPeriodRange,
      selectedPeriod,
      cycle,
    );
  }, [plot, selectedPeriod, datasetPeriodRange, cycle]);
};
